import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ROW_COUNT = 3;
const NOTE_COUNT = 3;

function renderParagraphs(text: unknown, className: string) {
  return String(text ?? '').split(/\n{2,}/).map((paragraph, index) => (
    <p key={index} className={className}>
      {paragraph}
    </p>
  ));
}

export default function TimeAndCost() {
  const { t } = useTranslation();

  const columns = ['course', 'duration', 'days', 'hours', 'price'] as const;

  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {renderParagraphs(
            t('butlerTrainingSchool.cost.label'),
            'text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label',
          )}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('butlerTrainingSchool.cost.title')}
          </h2>
          {renderParagraphs(
            t('butlerTrainingSchool.cost.titleEn'),
            'section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4',
          )}
          <div className="gold-line mx-auto mb-6"></div>
          {renderParagraphs(
            t('butlerTrainingSchool.cost.lead'),
            'text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed max-w-[720px] mx-auto',
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
          <div className="overflow-x-auto border border-background-200/60 bg-background-100/40">
            <table className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr className="bg-primary-900">
                  {columns.map((col) => (
                    <th
                      key={col}
                      className="text-left text-[14px] text-foreground-100 tracking-[0.06em] font-medium px-4 py-3.5 whitespace-nowrap"
                    >
                      {t(`butlerTrainingSchool.cost.col.${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: ROW_COUNT }, (_, idx) => (
                  <tr key={idx} className="border-t border-background-200/60">
                    {columns.map((col) => (
                      <td
                        key={col}
                        className={`px-4 py-3.5 text-[16px] tracking-[0.03em] whitespace-nowrap ${
                          col === 'course'
                            ? 'text-foreground-900 font-medium'
                            : 'text-foreground-600'
                        }`}
                      >
                        {t(`butlerTrainingSchool.cost.row${idx}.${col}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="relative w-full aspect-[4/3] overflow-hidden">
            <img
              src="/images/generated/pricing-included-2026.jpg"
              alt={t('butlerTrainingSchool.cost.imgAlt')}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
          {Array.from({ length: NOTE_COUNT }, (_, idx) => (
            <div
              key={idx}
              className="bg-background-100/60 border border-background-200/60 p-6 flex flex-col items-start"
            >
              <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-3">
                {t(`butlerTrainingSchool.cost.note${idx}.title`)}
              </h3>
              {renderParagraphs(
                t(`butlerTrainingSchool.cost.note${idx}.desc`),
                'text-[16px] text-foreground-500 tracking-[0.03em] leading-relaxed',
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/pricing/details"
            className="inline-flex items-center gap-2 text-[15px] text-accent-600 hover:text-accent-500 tracking-[0.05em] transition-colors"
          >
            <span>{t('butlerTrainingSchool.cost.priceLink')}</span>
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
